import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import {
  MainInformation,
  bannerValidationSchema,
  PostingDateRange,
  requestBodyData
} from "modules/offers";
import { ProductsFieldForForms } from "modules/products";
import { convertProductSetDetailsToListItem } from "modules/productSets";
import moment from "moment";
import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Checkbox, Modal } from "ui";

import { useBanner, useCreateBanner, useUpdateBanner } from "../../apis";
import {
  BannerFormValues,
  CreateBannerModalProps
} from "./CreateBannerModal.types";

import styles from "./CreateBannerModal.module.scss";

const NEW_OFFER_FORM_ID = "NEW_BANNER_FORM_ID";

const CreateBannerModal = ({
  isOpen,
  onClose,
  bannerId
}: CreateBannerModalProps) => {
  const refetchBanner = useRefetchQuery(["banner", `${bannerId}`]);
  const refetchBanners = useRefetchQuery(["banners"]);

  const { banner } = useBanner(bannerId);

  const {
    image = null,
    title = "",
    from = new Date(),
    to = new Date(),
    productVariants = [],
    productSet,
    allowDisplayInFullScreenMode = false
  } = banner ?? {};

  const defaultValues: BannerFormValues = {
    image,
    title,
    fromPostingDate: new Date(from),
    fromPostingTime: moment(from).format("HH:mm"),
    toPostingDate: new Date(to),
    toPostingTime: moment(to).format("HH:mm"),
    productVariants,
    productSet: convertProductSetDetailsToListItem(productSet),
    allowDisplayInFullScreenMode
  };

  const { control, watch, register, handleSubmit, formState, ...methods } =
    useForm<BannerFormValues>({
      ...(bannerId
        ? {
            values: defaultValues
          }
        : { defaultValues }),
      resolver: yupResolver(bannerValidationSchema)
    });

  const { mutate: mutateCreateBanner, isLoading: isLoadingCreateBanner } =
    useCreateBanner({
      onSuccess: () => {
        refetchBanners();
        onClose();
        methods.reset();
        toast.success(t("Banner was created successfully"));
      }
    });

  const { mutate: mutateUpdateBanner, isLoading: isLoadingUpdateBanner } =
    useUpdateBanner({
      onSuccess: () => {
        refetchBanners();
        refetchBanner();
        onClose();
        methods.reset();
        toast.success(t("Banner was updated successfully"));
      }
    });

  const onSubmit = (formData: BannerFormValues) => {
    const body = requestBodyData(formData, bannerId);

    if (bannerId) {
      mutateUpdateBanner({ id: bannerId, body });
    } else {
      mutateCreateBanner(body);
    }
  };

  return (
    <FormProvider
      {...{
        ...methods,
        control,
        watch,
        register,
        handleSubmit,
        formState
      }}
    >
      <Modal
        formId={NEW_OFFER_FORM_ID}
        isOpen={isOpen}
        title={`${bannerId ? "Edit" : "New"} Banner`}
        okText={bannerId ? "Save" : "Create"}
        onClose={onClose}
        isLoading={bannerId ? isLoadingCreateBanner : isLoadingUpdateBanner}
        onOk={handleSubmit(onSubmit)}
        className={styles.offer__modal}
        contentClassName={styles["offer__modal--content"]}
      >
        <MainInformation bannerType="Banner" />

        <Controller
          control={control}
          name="allowDisplayInFullScreenMode"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              label={t("Allow fullscreen for banner video")}
            />
          )}
        />

        <PostingDateRange />

        <Controller
          control={control}
          name={"productVariants"}
          render={({
            field: { value: productsList, onChange: onChangeProductsList },
            fieldState: { error: productsListError }
          }) => (
            <Controller
              control={control}
              name={"productSet"}
              render={({
                field: { value: productSetField, onChange: onChangeProductSet }
              }) => (
                <ProductsFieldForForms
                  productsList={productsList}
                  onChangeProductsList={onChangeProductsList}
                  productSet={productSetField}
                  errorMessage={productsListError?.message}
                  onChangeProductSet={onChangeProductSet}
                />
              )}
            />
          )}
        />
      </Modal>
    </FormProvider>
  );
};

export default CreateBannerModal;
